import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import {
  useQueries,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  ConnectionConfig,
  ConnectionInsert,
  ConnectionUpdate,
  ConnectionStatus,
  DeviceState,
  DeviceStateStore,
} from "@production-hub/shared";
import { getBridgeStoreEvents } from "@/lib/bridge-store-events";

export type {
  ConnectionConfig,
  ConnectionInsert,
  ConnectionUpdate,
  ConnectionStatus,
  DeviceState,
  DeviceStateStore,
};

export interface ConnectionWithStatus extends ConnectionConfig {
  status: ConnectionStatus;
}

interface BridgeStatusResponse {
  status: "ok" | "error";
  timestamp: string;
  connections: {
    total: number;
    enabled: number;
    connected: number;
    details: Array<{
      id: string;
      name: string;
      type: string;
      status: ConnectionStatus;
    }>;
  };
}

/**
 * Check if bridge is reachable
 */
export const useBridgeStatus = (bridgeUrl: string | undefined | null) => {
  return useQuery({
    queryKey: ["bridge-status", bridgeUrl],
    queryFn: async (): Promise<BridgeStatusResponse> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(`${bridgeUrl}/api/status`);
      if (!response.ok) {
        throw new Error(`Bridge returned ${response.status}`);
      }
      return response.json();
    },
    enabled: !!bridgeUrl,
    refetchInterval: 5000, // Poll every 5 seconds
    retry: false,
  });
};

/**
 * Fetch all connections from a bridge
 */
export const useBridgeConnections = (bridgeUrl: string | undefined | null) => {
  return useQuery({
    queryKey: ["bridge-connections", bridgeUrl],
    queryFn: async (): Promise<ConnectionWithStatus[]> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(`${bridgeUrl}/api/connections`);
      if (!response.ok) {
        throw new Error(`Failed to fetch connections: ${response.status}`);
      }
      return response.json();
    },
    enabled: !!bridgeUrl,
    refetchInterval: 5000, // Poll for status updates
  });
};

/**
 * Fetch a single connection from a bridge
 */
export const useBridgeConnection = (
  bridgeUrl: string | undefined | null,
  connectionId: string | undefined,
) => {
  return useQuery({
    queryKey: ["bridge-connections", bridgeUrl, connectionId],
    queryFn: async (): Promise<ConnectionWithStatus> => {
      if (!bridgeUrl) throw new Error("No bridge URL");
      if (!connectionId) throw new Error("No connection ID");

      const response = await fetch(
        `${bridgeUrl}/api/connections/${connectionId}`,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch connection: ${response.status}`);
      }
      return response.json();
    },
    enabled: !!bridgeUrl && !!connectionId,
    refetchInterval: 2000, // Poll faster for single connection
  });
};

/**
 * Create a new connection on a bridge
 */
export const useCreateBridgeConnection = (
  bridgeUrl: string | undefined | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      connection: ConnectionInsert,
    ): Promise<ConnectionWithStatus> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(`${bridgeUrl}/api/connections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(connection),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error || `Failed to create connection: ${response.status}`,
        );
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl],
      });
      queryClient.invalidateQueries({ queryKey: ["bridge-status", bridgeUrl] });
      toast.success("Connection created");
    },
    onError: (error) => {
      toast.error("Failed to create connection: " + error.message);
    },
  });
};

/**
 * Update a connection on a bridge
 */
export const useUpdateBridgeConnection = (
  bridgeUrl: string | undefined | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      connection,
    }: {
      id: string;
      connection: ConnectionUpdate;
    }): Promise<ConnectionWithStatus> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(`${bridgeUrl}/api/connections/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(connection),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error || `Failed to update connection: ${response.status}`,
        );
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl],
      });
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl, variables.id],
      });
      queryClient.invalidateQueries({ queryKey: ["bridge-status", bridgeUrl] });
      toast.success("Connection updated");
    },
    onError: (error) => {
      toast.error("Failed to update connection: " + error.message);
    },
  });
};

/**
 * Delete a connection from a bridge
 */
export const useDeleteBridgeConnection = (
  bridgeUrl: string | undefined | null,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(`${bridgeUrl}/api/connections/${id}`, {
        method: "DELETE",
      });

      if (!response.ok && response.status !== 204) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error || `Failed to delete connection: ${response.status}`,
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl],
      });
      queryClient.invalidateQueries({ queryKey: ["bridge-status", bridgeUrl] });
      toast.success("Connection deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete connection: " + error.message);
    },
  });
};

/**
 * Connect to a device
 */
export const useConnectDevice = (bridgeUrl: string | undefined | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<ConnectionWithStatus> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(
        `${bridgeUrl}/api/connections/${id}/connect`,
        {
          method: "POST",
        },
      );

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(error.error || `Failed to connect: ${response.status}`);
      }

      return response.json();
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl],
      });
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl, id],
      });
      queryClient.invalidateQueries({ queryKey: ["bridge-status", bridgeUrl] });
      toast.success("Connected to device");
    },
    onError: (error) => {
      toast.error("Failed to connect: " + error.message);
    },
  });
};

/**
 * Disconnect from a device
 */
export const useDisconnectDevice = (bridgeUrl: string | undefined | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<ConnectionWithStatus> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(
        `${bridgeUrl}/api/connections/${id}/disconnect`,
        {
          method: "POST",
        },
      );

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error || `Failed to disconnect: ${response.status}`,
        );
      }

      return response.json();
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl],
      });
      queryClient.invalidateQueries({
        queryKey: ["bridge-connections", bridgeUrl, id],
      });
      queryClient.invalidateQueries({ queryKey: ["bridge-status", bridgeUrl] });
      toast.success("Disconnected from device");
    },
    onError: (error) => {
      toast.error("Failed to disconnect: " + error.message);
    },
  });
};

/**
 * Get device state
 */
export const useDeviceState = (
  bridgeUrl: string | undefined | null,
  connectionId: string | undefined,
) => {
  return useQuery({
    queryKey: ["device-state", bridgeUrl, connectionId],
    queryFn: async (): Promise<DeviceState> => {
      if (!bridgeUrl) throw new Error("No bridge URL");
      if (!connectionId) throw new Error("No connection ID");

      const response = await fetch(
        `${bridgeUrl}/api/connections/${connectionId}/state`,
      );
      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error || `Failed to get state: ${response.status}`,
        );
      }

      return response.json();
    },
    enabled: !!bridgeUrl && !!connectionId,
    refetchInterval: 1000, // Poll frequently for live state
    retry: false,
  });
};

/**
 * Send a command to a device
 */
export interface DeviceCommand {
  action: string;
  [key: string]: unknown;
}

export const useSendCommand = (bridgeUrl: string | undefined | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      connectionId,
      command,
    }: {
      connectionId: string;
      command: DeviceCommand;
    }): Promise<{ success: boolean }> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(
        `${bridgeUrl}/api/connections/${connectionId}/command`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(command),
        },
      );

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(error.error || `Command failed: ${response.status}`);
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      // Invalidate device state to get fresh data
      queryClient.invalidateQueries({
        queryKey: ["device-state", bridgeUrl, variables.connectionId],
      });
    },
    onError: (error) => {
      toast.error("Command failed: " + error.message);
    },
  });
};

async function fetchDeviceStore(
  bridgeUrl: string,
  connectionId: string,
): Promise<DeviceStateStore> {
  const response = await fetch(
    `${bridgeUrl}/api/connections/${connectionId}/store`,
  );
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `Failed to get store: ${response.status}`);
  }
  const body = (await response.json()) as DeviceStateStore;
  return { values: body.values };
}

export interface BridgeDeviceStoreResult {
  data: DeviceStateStore | undefined;
  isLoading: boolean;
  isError: boolean;
}

const noSubscription = () => () => {};

/**
 * The live store of one device: kept current by the bridge's change stream
 * (`GET /api/events`, shared by all hooks of a bridge). `data` is a new
 * object only when something changed. Against a bridge without the stream
 * the full store is polled every second instead.
 */
export const useBridgeDeviceStore = (
  bridgeUrl: string | undefined | null,
  connectionId: string | undefined,
): BridgeDeviceStoreResult => {
  const events =
    bridgeUrl && connectionId ? getBridgeStoreEvents(bridgeUrl) : null;
  const subscribe = useCallback(
    (listener: () => void) =>
      events && connectionId
        ? events.subscribe(connectionId, listener)
        : noSubscription(),
    [events, connectionId],
  );
  const view = useSyncExternalStore(subscribe, () =>
    events && connectionId ? events.view(connectionId) : null,
  );
  const polling = view?.mode === "unsupported";
  const query = useQuery({
    queryKey: ["device-store", bridgeUrl, connectionId],
    queryFn: () => fetchDeviceStore(bridgeUrl!, connectionId!),
    enabled: polling,
    refetchInterval: 1000,
    retry: false,
  });
  if (!view) return { data: undefined, isLoading: false, isError: false };
  if (polling) {
    return {
      data: query.data,
      isLoading: query.isLoading,
      isError: query.isError,
    };
  }
  return {
    data: view.data,
    isLoading: !view.data && !view.unavailable && view.mode !== "reconnecting",
    isError: !view.data && (view.unavailable || view.mode === "reconnecting"),
  };
};

/**
 * Live stores of several devices of a bridge (signal tracing), from the same
 * stream; polled every 3 s against a bridge without it.
 */
export function useBridgeDeviceStores(
  bridgeUrl: string | undefined | null,
  connectionIds: readonly string[],
): Array<DeviceStateStore | undefined> {
  const events = bridgeUrl ? getBridgeStoreEvents(bridgeUrl) : null;
  const key = connectionIds.join(",");
  const subscribe = useCallback(
    (listener: () => void) => {
      if (!events || !key) return () => {};
      const offs = key.split(",").map((id) => events.subscribe(id, listener));
      return () => offs.forEach((off) => off());
    },
    [events, key],
  );
  const last = useRef<{
    polling: boolean;
    stores: Array<DeviceStateStore | undefined>;
  }>({ polling: false, stores: [] });
  const live = useSyncExternalStore(subscribe, () => {
    const views = connectionIds.map((id) => events?.view(id));
    const polling = views[0]?.mode === "unsupported";
    const stores = views.map((view) => view?.data);
    const previous = last.current;
    if (
      polling === previous.polling &&
      stores.length === previous.stores.length &&
      stores.every((data, i) => data === previous.stores[i])
    )
      return previous;
    return (last.current = { polling, stores });
  });
  const polling = live.polling;
  const results = useQueries({
    queries: connectionIds.map((id) => ({
      queryKey: ["device-store", bridgeUrl, id],
      queryFn: () => fetchDeviceStore(bridgeUrl!, id),
      enabled: polling,
      refetchInterval: 3000,
      retry: false,
    })),
  });
  const polledKey = results
    .map((result) => (result.isError ? "x" : result.dataUpdatedAt))
    .join(",");
  const polled = useMemo(
    () => results.map((result) => (result.isError ? undefined : result.data)),
    // results change identity every render; polledKey tracks their data
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [polledKey],
  );
  return polling ? polled : live.stores;
}

/**
 * Update a single value in the device state store
 */
/** Wait after the last store update before refetching the store (polling) */
const STORE_REFETCH_DELAY_MS = 250;

export const useUpdateBridgeDeviceStore = (
  bridgeUrl: string | undefined | null,
) => {
  const queryClient = useQueryClient();
  const refetchTimers = useRef(
    new Map<string, ReturnType<typeof setTimeout>>(),
  );
  useEffect(() => {
    const timers = refetchTimers.current;
    return () => timers.forEach((t) => clearTimeout(t));
  }, []);

  return useMutation({
    mutationFn: async ({
      connectionId,
      path,
      value,
    }: {
      connectionId: string;
      path: string;
      value: string | number | boolean;
    }): Promise<{ success: boolean }> => {
      if (!bridgeUrl) throw new Error("No bridge URL");

      const response = await fetch(
        `${bridgeUrl}/api/connections/${connectionId}/store`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ path, value }),
        },
      );

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: "Unknown error" }));
        throw new Error(
          error.error || `Store update failed: ${response.status}`,
        );
      }

      return response.json();
    },
    // Shown at once; the change stream confirms it (no refetch)
    onMutate: ({ connectionId, path, value }) => {
      if (!bridgeUrl) return undefined;
      const events = getBridgeStoreEvents(bridgeUrl);
      if (events.view(connectionId).mode === "unsupported") return undefined;
      return { token: events.write(connectionId, path, value) };
    },
    onSettled: (_data, error, variables, context) => {
      if (!bridgeUrl || !context) return;
      getBridgeStoreEvents(bridgeUrl).settle(
        variables.connectionId,
        variables.path,
        context.token,
        !error,
      );
    },
    onSuccess: (_, variables, context) => {
      if (context) return;
      // Polling an older bridge: a dragged fader sends ~12 times a second;
      // refetch the (large) store once it settles rather than every step
      const key = `${bridgeUrl}|${variables.connectionId}`;
      clearTimeout(refetchTimers.current.get(key));
      refetchTimers.current.set(
        key,
        setTimeout(() => {
          refetchTimers.current.delete(key);
          queryClient.invalidateQueries({
            queryKey: ["device-store", bridgeUrl, variables.connectionId],
          });
        }, STORE_REFETCH_DELAY_MS),
      );
    },
    onError: (error) => {
      toast.error("Store update failed: " + error.message);
    },
  });
};
